import {
  AlertCircle,
  Clock,
  Image as ImageIcon,
  RefreshCw,
  Save,
  Upload,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTenant } from '@/features/tenants/contexts/TenantContext'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { TIMEZONES } from '@/shared/types/timezone'

interface AccountDetailsFormProps {
  onSuccess?: () => void
}

export function AccountDetailsForm({ onSuccess }: AccountDetailsFormProps) {
  const { currentTenant, updateTenant, isUpdatingTenant } = useTenant()
  const [name, setName] = useState(currentTenant?.name || '')
  const [logoUrl, setLogoUrl] = useState(currentTenant?.logo_url || '')
  const [timezone, setTimezone] = useState(currentTenant?.timezone || 'UTC')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [hasChanges, setHasChanges] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update local state when tenant changes
  useEffect(() => {
    if (currentTenant) {
      setName(currentTenant.name)
      setLogoUrl(currentTenant.logo_url || '')
      setTimezone(currentTenant.timezone)
    }
  }, [currentTenant])

  // Track if there are any changes
  useEffect(() => {
    const hasChanges =
      name !== currentTenant?.name ||
      logoUrl !== (currentTenant?.logo_url || '') ||
      timezone !== currentTenant?.timezone
    setHasChanges(hasChanges)
  }, [name, logoUrl, timezone, currentTenant])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    // Validate organization name
    if (!name?.trim()) {
      newErrors.name = 'Organization name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    try {
      await updateTenant({
        name: name.trim(),
        ...(logoUrl && { logo_url: logoUrl }),
        settings: { timezone },
      })

      setHasChanges(false)
      onSuccess?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
      setErrors({ submit: 'Failed to update account. Please try again.' })
    }
  }

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // In a real app, you would upload to a file service
      const reader = new FileReader()
      reader.onload = e => {
        const result = e.target?.result as string
        setLogoUrl(result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = () => {
    setLogoUrl('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='max-w-3xl mx-auto p-6'>
        <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
          <div>
            <PageTitle
              title='Account Settings'
              subtitle='Manage your account preferences and profile information'
            />
          </div>
        </div>
      </div>

      {/* Form */}
      <div className='max-w-3xl mx-auto px-6'>
        <Card className='bg-transparent'>
          <CardContent className='space-y-10 pt-6'>
            {/* Account ID */}
            <div className='space-y-2'>
              <Label htmlFor='account_id'>Account ID</Label>
              <div className='font-mono text-sm text-muted-foreground'>
                {currentTenant?.id}
              </div>
            </div>

            {/* Organization Name */}
            <div className='space-y-2'>
              <Label htmlFor='organization_name'>
                Organization Name <span className='text-red-500'>*</span>
              </Label>
              <div className='w-1/2'>
                <Input
                  id='organization_name'
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder='Your organization name'
                  className={errors.name ? 'border-red-500 mt-3' : 'mt-3'}
                />
                {errors.name && (
                  <div className='flex items-center gap-1 text-sm text-red-600'>
                    <AlertCircle className='w-4 h-4' />
                    {errors.name}
                  </div>
                )}
              </div>
            </div>

            {/* Organization Logo */}
            <div className='space-y-2'>
              <Label>Organization Logo</Label>
              <div className='space-y-3 mt-3'>
                {logoUrl ? (
                  <div className='relative w-fit'>
                    <div className='w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50'>
                      <img
                        src={logoUrl}
                        alt='Organization logo'
                        className='w-full h-full object-cover'
                      />
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={handleRemoveLogo}
                      className='absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full'
                    >
                      <X className='w-3 h-3' />
                    </Button>
                  </div>
                ) : (
                  // biome-ignore lint/a11y/useSemanticElements: styled upload area
                  <div
                    className='w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors'
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ')
                        fileInputRef.current?.click()
                    }}
                    role='button'
                    tabIndex={0}
                  >
                    <div className='text-center'>
                      <ImageIcon className='w-6 h-6 text-gray-400 mx-auto mb-1' />
                      <p className='text-xs text-gray-500'>Upload</p>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  onChange={handleLogoUpload}
                  className='hidden'
                />
                <div className='flex gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className='w-4 h-4 mr-1' />
                    {logoUrl ? 'Change' : 'Upload'}
                  </Button>
                  {logoUrl && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={handleRemoveLogo}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <p className='text-xs text-muted-foreground'>
                  PNG, JPG up to 2MB. Recommended size: 200x200px
                </p>
              </div>
            </div>

            {/* Timezone */}
            <div className='space-y-2'>
              <Label htmlFor='timezone'>
                <Clock className='inline w-4 h-4 mr-1' />
                Time Zone
              </Label>
              <div className='w-1/2'>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger className='mt-3'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='max-h-[200px]'>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        <div className='flex justify-between w-full'>
                          <span>{tz.label}</span>
                          <span className='text-muted-foreground ml-2'>
                            {tz.offset}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Save Button */}
            <div className='flex justify-end pt-4'>
              <Button
                onClick={handleSave}
                disabled={isUpdatingTenant || !hasChanges}
                size='lg'
              >
                {isUpdatingTenant ? (
                  <>
                    <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className='mr-2 h-4 w-4' />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {errors.submit && (
          <div className='flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200'>
            <AlertCircle className='w-4 h-4 text-red-600' />
            <span className='text-sm text-red-600'>{errors.submit}</span>
          </div>
        )}
      </div>
    </div>
  )
}
